import React from "react";
import { motion } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro";
import { Link } from 'react-router-dom';
import useAnimatedNavToggler from "../../helpers/useAnimatedNavToggler.js"; // Ensure this path is correct
import logo from "../../images/new-log.png";
import { ReactComponent as MenuIcon } from "feather-icons/dist/icons/menu.svg";
import { ReactComponent as CloseIcon } from "feather-icons/dist/icons/x.svg";

export const Header = tw.header`
  flex justify-between items-center
  max-w-screen-xl mx-auto
`;

export const NavLinks = tw.div`inline-block`;

export const NavLink = tw.a`
  text-lg my-2 lg:text-sm lg:mx-6 lg:my-0
  transition duration-300
  pb-1 border-b-2 border-transparent
`;

export const PrimaryLink = tw(NavLink)`
  lg:mx-0
  px-8 py-3 rounded bg-[#0049AC] text-white
  border-b-0
`;

export const LogoLink = styled(NavLink)`
  ${tw`flex items-center font-black border-b-0 text-2xl! ml-0!`};
  img {
    ${tw`w-40 mr-3`}
  }
`;

export const MobileNavLinksContainer = tw.nav`flex flex-1 items-center justify-between`;

export const NavToggle = tw.button`
  lg:hidden z-20 focus:outline-none focus:text-[#0049AC] p-4 transition duration-300
`;

export const MobileNavLinks = motion(styled.div`
  ${tw`bg-[#0049AC] lg:hidden z-10 fixed top-0 bottom-0 inset-x-0 mx-4 my-6 p-6 rounded-lg text-center text-white flex flex-col space-y-4 overflow-y-auto max-h-screen`}
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  ${NavLinks} {
    ${tw`flex flex-col space-y-4`}
  }
`);

export const DesktopNavLinks = tw.nav`
  hidden lg:flex flex-1 justify-between items-center
`;

const collapseBreakPointCssMap = {
  sm: {
    mobileNavLinks: tw`sm:hidden`,
    desktopNavLinks: tw`sm:flex`,
    mobileNavLinksContainer: tw`sm:hidden`
  },
  md: {
    mobileNavLinks: tw`md:hidden`,
    desktopNavLinks: tw`md:flex`,
    mobileNavLinksContainer: tw`md:hidden`
  },
  lg: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`
  },
  xl: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`
  }
};

const DefaultHeader = ({ roundedHeaderButton = true, logoLink, links, className, collapseBreakpointClass = "lg", onToggleNavbar }) => {
  const { showNavLinks, animation, toggleNavbar } = useAnimatedNavToggler();
  const collapseBreakpointCss = collapseBreakPointCssMap[collapseBreakpointClass];

  const defaultLogoLink = (
    <LogoLink as={Link} to="/">
      <img src={logo} alt="logo" />
      <span>Traviator Holidays</span>
    </LogoLink>
  );

  logoLink = logoLink || defaultLogoLink;

  const renderedLinks = Array.isArray(links) ? links.map(link => 
    React.cloneElement(link, { toggleNavbar })
  ) : links;

  const handleToggle = () => {
    if (showNavLinks) {
      document.body.style.overflow = 'auto';
    } else {
      document.body.style.overflow = 'hidden';
    }
    toggleNavbar();
    if (onToggleNavbar) onToggleNavbar(!showNavLinks);
  };

  return (
    <Header className={className || "header-light"} toggleNavbar={toggleNavbar}>
      <DesktopNavLinks css={collapseBreakpointCss.desktopNavLinks}>
        {logoLink}
        {renderedLinks}
      </DesktopNavLinks>

      <MobileNavLinksContainer css={collapseBreakpointCss.mobileNavLinksContainer}>
        {logoLink}
        <MobileNavLinks initial={{ x: "150%", display: "none" }} animate={animation}>
          {renderedLinks}
        </MobileNavLinks>
        <NavToggle onClick={handleToggle} className={showNavLinks ? "open" : "closed"}>
          {showNavLinks ? <CloseIcon tw="w-6 h-6 text-white" /> : <MenuIcon tw="w-10 h-10 text-white" />}
        </NavToggle>
      </MobileNavLinksContainer>
    </Header>
  );
};

export default DefaultHeader;